import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import storeApi from '../services/storeApi';
import authService from '../../services/api/authService';
import { useToast } from '../../contexts/ToastContext';

/**
 * CheckoutPage - Checkout page with billing and shipping forms
 * Follows React Rules: Side effects in useEffect, proper state management
 */
const CheckoutPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [shipToDifferent, setShipToDifferent] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [submitting, setSubmitting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loginError, setLoginError] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  const [billingData, setBillingData] = useState({
    country: 'Pakistan',
    firstName: '',
    lastName: '',
    companyName: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    email: '',
    phone: ''
  });

  const [shippingData, setShippingData] = useState({
    country: 'Pakistan',
    firstName: '',
    lastName: '',
    companyName: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    email: '',
    phone: ''
  });

  const [orderNotes, setOrderNotes] = useState('');

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      setIsAuthenticated(!!token);
    };
    checkAuth();
    // Listen for storage changes (e.g., login from another tab)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const cartData = await storeApi.getCart();
        setCart(cartData);
        // Update auth status after successful cart fetch
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error fetching cart:', error);
        if (error.response?.status === 401) {
          setIsAuthenticated(false);
          // Don't redirect, let user stay on checkout page
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCart();
  }, [navigate]);

  // Unbind jQuery handlers that conflict with React state management
  useEffect(() => {
    // Wait for jQuery and main.js to load
    const unbindHandlers = () => {
      if (window.jQuery) {
        // Unbind ALL event handlers for showlogin and showcoupon
        window.jQuery('#showlogin').off();
        window.jQuery('#showcoupon').off();
        // Also unbind any delegated handlers
        window.jQuery(document).off('click', '#showlogin');
        window.jQuery(document).off('click', '#showcoupon');
      } else {
        // Retry if jQuery not loaded yet
        setTimeout(unbindHandlers, 100);
      }
    };

    // Try to unbind immediately, then retry multiple times to ensure main.js has run
    unbindHandlers();
    const timeoutId1 = setTimeout(unbindHandlers, 300);
    const timeoutId2 = setTimeout(unbindHandlers, 600);
    const timeoutId3 = setTimeout(unbindHandlers, 1000);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, []);

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingData(prev => ({ ...prev, [name]: value }));
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setLoginError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginSubmitting(true);
    setLoginError('');

    try {
      await authService.login({
        email: loginData.email,
        password: loginData.password
      });

      setIsAuthenticated(true);
      window.dispatchEvent(new Event('userLoggedIn'));

      try {
        const cartData = await storeApi.getCart();
        setCart(cartData);
      } catch (cartError) {
        // Cart refresh non-blocking
      }

      setShowLogin(false);
      setLoginData({ email: '', password: '', rememberMe: false });
      showSuccess('Login successful!');
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Invalid email or password. Please try again.';
      setLoginError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const orderData = {
        shipping_name: `${billingData.firstName} ${billingData.lastName}`,
        shipping_address: billingData.address,
        shipping_address2: billingData.address2,
        shipping_city: billingData.city,
        shipping_state: billingData.state,
        shipping_postal_code: billingData.postalCode,
        shipping_phone: billingData.phone,
        shipping_email: billingData.email,
        notes: orderNotes
      };

      if (shipToDifferent) {
        orderData.shipping_name = `${shippingData.firstName} ${shippingData.lastName}`;
        orderData.shipping_address = shippingData.address;
        orderData.shipping_address2 = shippingData.address2;
        orderData.shipping_city = shippingData.city;
        orderData.shipping_state = shippingData.state;
        orderData.shipping_postal_code = shippingData.postalCode;
        orderData.shipping_phone = shippingData.phone;
        orderData.shipping_email = shippingData.email;
      }

      const order = await storeApi.createOrder(orderData);
      showSuccess('Order placed successfully!');
      navigate(`/store/orders/${order.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      const errorMessage = error.response?.data?.detail || 
        error.response?.data?.message || 
        'Error placing order. Please try again.';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Loading checkout...</p>
        </div>
      </PageContainer>
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Your cart is empty.</p>
          <Link to="/store/shop" className="btn">Continue Shopping</Link>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <div className="breadcrumb-area">
        <div className="container">
          <div className="breadcrumb-content">
            <ul>
              <li><Link to="/store">Home</Link></li>
              <li className="active">Checkout</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Checkout Area */}
      <div className="checkout-area pt-60 pb-30">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="coupon-accordion">
                {/* Login Accordion - Only show if not authenticated */}
                {!isAuthenticated && (
                  <>
                    <h3>
                      Returning customer?{' '}
                      <span
                        id="showlogin"
                        style={{ cursor: 'pointer', color: '#007bff' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowLogin(!showLogin);
                        }}
                      >
                        Click here to login
                      </span>
                    </h3>
                    {showLogin && (
                  <div id="checkout-login" className="coupon-content" style={{ display: 'block' }}>
                    <div className="coupon-info">
                      <p className="coupon-text">
                        If you have shopped with us before, please enter your details below. If you are a new customer, please proceed to the Billing section.
                      </p>
                      {loginError && (
                        <div style={{ color: '#e80f0f', marginBottom: '15px', padding: '10px', background: '#ffe6e6', borderRadius: '4px' }}>
                          {loginError}
                        </div>
                      )}
                      <form onSubmit={handleLogin}>
                        <p className="form-row-first">
                          <label>Email address <span className="required">*</span></label>
                          <input 
                            type="email" 
                            name="email"
                            value={loginData.email}
                            onChange={handleLoginChange}
                            required
                          />
                        </p>
                        <p className="form-row-last">
                          <label>Password <span className="required">*</span></label>
                          <input 
                            type="password" 
                            name="password"
                            value={loginData.password}
                            onChange={handleLoginChange}
                            required
                          />
                        </p>
                        <p className="form-row">
                          <input 
                            value={loginSubmitting ? "Logging in..." : "Login"} 
                            type="submit" 
                            disabled={loginSubmitting}
                          />
                          <label>
                            <input 
                              type="checkbox" 
                              name="rememberMe"
                              checked={loginData.rememberMe}
                              onChange={handleLoginChange}
                            />
                            Remember me
                          </label>
                        </p>
                        <p className="lost-password">
                          <Link to="/store/login-register">Lost your password?</Link>
                        </p>
                      </form>
                    </div>
                  </div>
                    )}
                  </>
                )}

                {/* Coupon Accordion */}
                <h3>
                  Have a coupon?{' '}
                  <span
                    id="showcoupon"
                    style={{ cursor: 'pointer', color: '#007bff' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowCoupon(!showCoupon);
                    }}
                  >
                    Click here to enter your code
                  </span>
                </h3>
                {showCoupon && (
                  <div id="checkout_coupon" className="coupon-checkout-content">
                    <div className="coupon-info">
                      <form action="#">
                        <p className="checkout-coupon">
                          <input placeholder="Coupon code" type="text" />
                          <input value="Apply Coupon" type="submit" />
                        </p>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Billing Details */}
              <div className="col-lg-6 col-12">
                <div className="checkbox-form">
                  <h3>Billing Details</h3>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="checkout-form-list">
                        <label>Country <span className="required">*</span></label>
                        <select
                          name="country"
                          value={billingData.country}
                          onChange={handleBillingChange}
                          className="nice-select wide"
                          required
                        >
                          <option value="Pakistan">Pakistan</option>
                          <option value="USA">USA</option>
                          <option value="UK">UK</option>
                          <option value="Canada">Canada</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-form-list">
                        <label>First Name <span className="required">*</span></label>
                        <input
                          type="text"
                          name="firstName"
                          value={billingData.firstName}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-form-list">
                        <label>Last Name <span className="required">*</span></label>
                        <input
                          type="text"
                          name="lastName"
                          value={billingData.lastName}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="checkout-form-list">
                        <label>Company Name</label>
                        <input
                          type="text"
                          name="companyName"
                          value={billingData.companyName}
                          onChange={handleBillingChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="checkout-form-list">
                        <label>Address <span className="required">*</span></label>
                        <input
                          type="text"
                          name="address"
                          placeholder="Street address"
                          value={billingData.address}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="checkout-form-list">
                        <input
                          type="text"
                          name="address2"
                          placeholder="Apartment, suite, unit etc. (optional)"
                          value={billingData.address2}
                          onChange={handleBillingChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="checkout-form-list">
                        <label>Town / City <span className="required">*</span></label>
                        <input
                          type="text"
                          name="city"
                          value={billingData.city}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-form-list">
                        <label>State / County <span className="required">*</span></label>
                        <input
                          type="text"
                          name="state"
                          value={billingData.state}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-form-list">
                        <label>Postcode / Zip <span className="required">*</span></label>
                        <input
                          type="text"
                          name="postalCode"
                          value={billingData.postalCode}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-form-list">
                        <label>Email Address <span className="required">*</span></label>
                        <input
                          type="email"
                          name="email"
                          value={billingData.email}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="checkout-form-list">
                        <label>Phone <span className="required">*</span></label>
                        <input
                          type="text"
                          name="phone"
                          value={billingData.phone}
                          onChange={handleBillingChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="checkout-form-list create-acc">
                        <input
                          id="cbox"
                          type="checkbox"
                          checked={createAccount}
                          onChange={(e) => setCreateAccount(e.target.checked)}
                        />
                        <label htmlFor="cbox">Create an account?</label>
                      </div>
                      {createAccount && (
                        <div id="cbox-info" className="checkout-form-list create-account">
                          <p>
                            Create an account by entering the information below. If you are a returning customer please login at the top of the page.
                          </p>
                          <label>Account password <span className="required">*</span></label>
                          <input placeholder="password" type="password" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ship to Different Address */}
                  <div className="different-address">
                    <div className="ship-different-title">
                      <h3>
                        <label htmlFor="ship-box" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                          <input
                            id="ship-box"
                            type="checkbox"
                            checked={shipToDifferent}
                            onChange={(e) => setShipToDifferent(e.target.checked)}
                            style={{ margin: 0, width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span>Ship to a different address?</span>
                        </label>
                      </h3>
                    </div>
                    {shipToDifferent && (
                      <div id="ship-box-info" className="row" style={{ display: 'block' }}>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>Country <span className="required">*</span></label>
                            <select
                              name="country"
                              value={shippingData.country}
                              onChange={handleShippingChange}
                              className="nice-select wide"
                              required
                            >
                              <option value="Pakistan">Pakistan</option>
                              <option value="USA">USA</option>
                              <option value="UK">UK</option>
                              <option value="Canada">Canada</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>First Name <span className="required">*</span></label>
                            <input
                              type="text"
                              name="firstName"
                              value={shippingData.firstName}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>Last Name <span className="required">*</span></label>
                            <input
                              type="text"
                              name="lastName"
                              value={shippingData.lastName}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>Address <span className="required">*</span></label>
                            <input
                              type="text"
                              name="address"
                              placeholder="Street address"
                              value={shippingData.address}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <input
                              type="text"
                              name="address2"
                              placeholder="Apartment, suite, unit etc. (optional)"
                              value={shippingData.address2}
                              onChange={handleShippingChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>Town / City <span className="required">*</span></label>
                            <input
                              type="text"
                              name="city"
                              value={shippingData.city}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>State / County <span className="required">*</span></label>
                            <input
                              type="text"
                              name="state"
                              value={shippingData.state}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>Postcode / Zip <span className="required">*</span></label>
                            <input
                              type="text"
                              name="postalCode"
                              value={shippingData.postalCode}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="checkout-form-list">
                            <label>Phone <span className="required">*</span></label>
                            <input
                              type="text"
                              name="phone"
                              value={shippingData.phone}
                              onChange={handleShippingChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="order-notes">
                      <div className="checkout-form-list">
                        <label>Order Notes</label>
                        <textarea
                          id="checkout-mess"
                          cols="30"
                          rows="10"
                          placeholder="Notes about your order, e.g. special notes for delivery."
                          value={orderNotes}
                          onChange={(e) => setOrderNotes(e.target.value)}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="col-lg-6 col-12">
                <div className="your-order">
                  <h3>Your order</h3>
                  <div className="your-order-table table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="cart-product-name">Product</th>
                          <th className="cart-product-total">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart.items.map((item) => (
                          <tr key={item.id} className="cart_item">
                            <td className="cart-product-name">
                              {item.product.name}
                              <strong className="product-quantity"> × {item.quantity}</strong>
                            </td>
                            <td className="cart-product-total">
                              <span className="amount">PKR {parseFloat(item.subtotal).toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="cart-subtotal">
                          <th>Cart Subtotal</th>
                          <td>
                            <span className="amount">PKR {parseFloat(cart.subtotal || 0).toFixed(2)}</span>
                          </td>
                        </tr>
                        <tr className="order-total">
                          <th>Order Total</th>
                          <td>
                            <strong>
                              <span className="amount">PKR {parseFloat(cart.subtotal || 0).toFixed(2)}</span>
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Payment Method */}
                  <div className="payment-method">
                    <div className="payment-accordion">
                      <div id="accordion">
                        <div className="card">
                          <div className="card-header" id="#payment-1">
                            <h5 className="panel-title">
                              <label>
                                <input
                                  type="radio"
                                  name="payment"
                                  value="bank_transfer"
                                  checked={paymentMethod === 'bank_transfer'}
                                  onChange={(e) => setPaymentMethod(e.target.value)}
                                />
                                Direct Bank Transfer
                              </label>
                            </h5>
                          </div>
                          {paymentMethod === 'bank_transfer' && (
                            <div className="card-body">
                              <p>
                                Make your payment directly into our bank account. Please use your Order ID as the payment reference. 
                                Your order won't be shipped until the funds have cleared in our account.
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="card">
                          <div className="card-header" id="#payment-2">
                            <h5 className="panel-title">
                              <label>
                                <input
                                  type="radio"
                                  name="payment"
                                  value="cash_on_delivery"
                                  checked={paymentMethod === 'cash_on_delivery'}
                                  onChange={(e) => setPaymentMethod(e.target.value)}
                                />
                                Cash on Delivery
                              </label>
                            </h5>
                          </div>
                          {paymentMethod === 'cash_on_delivery' && (
                            <div className="card-body">
                              <p>
                                Pay with cash when your order is delivered. Please have the exact amount ready for the delivery person.
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="card">
                          <div className="card-header" id="#payment-3">
                            <h5 className="panel-title">
                              <label>
                                <input
                                  type="radio"
                                  name="payment"
                                  value="paypal"
                                  checked={paymentMethod === 'paypal'}
                                  onChange={(e) => setPaymentMethod(e.target.value)}
                                />
                                PayPal
                              </label>
                            </h5>
                          </div>
                          {paymentMethod === 'paypal' && (
                            <div className="card-body">
                              <p>
                                Pay via PayPal; you can pay with your credit card if you don't have a PayPal account.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="order-button-payment">
                    <button
                      type="submit"
                      className="btn"
                      disabled={submitting}
                    >
                      {submitting ? 'Placing Order...' : 'Place order'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </PageContainer>
  );
};

export default CheckoutPage;
